// The family tree's connectors, worked out from where the people actually landed on screen. Every line starts at the
// person in the middle and runs out to one neighbor along right angles with softened corners, the way a hand-drawn
// family tree reads: parents up through a shared bar, children down from a shared bar, brothers and sisters off one
// side and partners off the other. Grandparents rise from the top of their child's tile and grandchildren drop from
// the bottom of their parent's, the same way. Family friends aren't drawn; they sit nearby under their own heading.

export type Box = { x: number; y: number; w: number; h: number }
// A person in the orbit: their whole tile (face, name and label) and just their face.
export type Placed = { id: string; tile: Box; face: Box }
export type Layout = {
  face: Box // the face of the person in the middle
  block: Box // everything under it too: name, dates and the add button
  parents: Placed[]
  siblings: Placed[]
  partners: Placed[]
  children: Placed[]
  // A generation further out, each through the parent or child in between.
  grandparents?: (Placed & { via: string })[]
  grandchildren?: (Placed & { via: string })[]
}
export type Connector = { id: string; d: string }
type Point = [number, number]

const cx = (b: Box) => b.x + b.w / 2
const cy = (b: Box) => b.y + b.h / 2
const right = (b: Box) => b.x + b.w
const bottom = (b: Box) => b.y + b.h
const near = (a: number, b: number) => Math.abs(a - b) < 0.5
const n = (v: number) => Math.round(v * 10) / 10

// A path through right-angled points with each corner rounded, never by more than half of either side.
export function roundedPath(points: Point[], radius = 12): string {
  const pts: Point[] = []
  for (const p of points) {
    const last = pts.at(-1)
    if (last && near(last[0], p[0]) && near(last[1], p[1])) continue
    // A point on the straight line between its neighbors is no corner.
    const before = pts.at(-2)
    if (
      before &&
      last &&
      ((near(before[0], last[0]) && near(last[0], p[0])) || (near(before[1], last[1]) && near(last[1], p[1])))
    )
      pts.pop()
    pts.push(p)
  }
  if (pts.length === 0) return ''
  let d = `M${n(pts[0]![0])} ${n(pts[0]![1])}`
  for (let i = 1; i < pts.length; i++) {
    const [x, y] = pts[i]!
    const next = pts[i + 1]
    if (!next) {
      d += ` L${n(x)} ${n(y)}`
      break
    }
    const [px, py] = pts[i - 1]!
    const inLen = Math.hypot(x - px, y - py)
    const outLen = Math.hypot(next[0] - x, next[1] - y)
    const r = Math.min(radius, inLen / 2, outLen / 2)
    const ax = x - ((x - px) / inLen) * r
    const ay = y - ((y - py) / inLen) * r
    const bx = x + ((next[0] - x) / outLen) * r
    const by = y + ((next[1] - y) / outLen) * r
    d += ` L${n(ax)} ${n(ay)} Q${n(x)} ${n(y)} ${n(bx)} ${n(by)}`
  }
  return d
}

// People in the same row of a wrapped list, top row first.
function rows(placed: Placed[]): Placed[][] {
  const out: Placed[][] = []
  for (const p of [...placed].sort((a, b) => a.face.y - b.face.y || a.face.x - b.face.x)) {
    const row = out.at(-1)
    if (row && Math.abs(row[0]!.face.y - p.face.y) < 8) row.push(p)
    else out.push([p])
  }
  return out
}

export function connectors(l: Layout, radius = 12): Connector[] {
  const out: Connector[] = []
  const fx = cx(l.face)
  const fy = cy(l.face)

  // Parents: up from the face to a bar halfway, then up to each parent.
  if (l.parents.length > 0) {
    const bar = (Math.max(...l.parents.map((p) => bottom(p.tile))) + l.face.y) / 2
    for (const p of l.parents) {
      const d = roundedPath(
        [
          [fx, l.face.y],
          [fx, bar],
          [cx(p.face), bar],
          [cx(p.face), bottom(p.tile)],
        ],
        radius,
      )
      out.push({ id: p.id, d })
    }
  }

  // Brothers and sisters to the left and partners to the right, each off a spine in the gap between.
  const side = (people: Placed[], leftward: boolean) => {
    if (people.length === 0) return
    const edge = leftward ? l.face.x : right(l.face)
    const far = leftward ? Math.max(...people.map((p) => right(p.face))) : Math.min(...people.map((p) => p.face.x))
    // Halfway across the gap, but kept close to them on a narrow screen so it runs clear of the middle's name.
    const spine = leftward ? Math.min((edge + far) / 2, far + 14) : Math.max((edge + far) / 2, far - 14)
    for (const p of people) {
      const d = roundedPath(
        [
          [edge, fy],
          [spine, fy],
          [spine, cy(p.face)],
          [leftward ? right(p.face) : p.face.x, cy(p.face)],
        ],
        radius,
      )
      out.push({ id: p.id, d })
    }
  }
  side(l.siblings, true)
  side(l.partners, false)

  // Children: down from under the middle to a bar above each row.
  fan(out, l.children, [fx, bottom(l.block)], bottom(l.block), true, radius)

  // Grandparents: up from the top of their child's tile, the way parents rise from the middle.
  const top = l.parents.length ? Math.min(...l.parents.map((p) => p.tile.y)) : 0
  for (const p of l.parents) {
    const theirs = new Set((l.grandparents ?? []).filter((g) => g.via === p.id).map((g) => g.id))
    if (theirs.size) fan(out, l.grandparents!, [cx(p.face), p.tile.y], top, false, radius, theirs)
  }

  // Grandchildren: down from the bottom of their parent's tile.
  const low = l.children.length ? Math.max(...l.children.map((p) => bottom(p.tile))) : 0
  for (const c of l.children) {
    const theirs = new Set((l.grandchildren ?? []).filter((g) => g.via === c.id).map((g) => g.id))
    if (theirs.size) fan(out, l.grandchildren!, [cx(c.face), bottom(c.tile)], low, true, radius, theirs)
  }
  return out
}

// Lines from one point out to a row of people above or below it: to a bar halfway across the gap from edge, then
// along it to each of them. When the row wraps, the line crosses to the next row through the gap nearest where it
// started, so it never runs through anyone. Only those in only get a line; everyone in placed shapes the bars.
function fan(
  out: Connector[],
  placed: Placed[],
  [ox, oy]: Point,
  edge: number,
  down: boolean,
  radius: number,
  only?: Set<string>,
) {
  if (placed.length === 0) return
  const grid = down ? rows(placed) : rows(placed).reverse()
  // The side of a row facing where the lines come from, and the side facing away.
  const inner = (row: Placed[]) =>
    down ? Math.min(...row.map((p) => p.tile.y)) : Math.max(...row.map((p) => bottom(p.tile)))
  const outer = (row: Placed[]) =>
    down ? Math.max(...row.map((p) => bottom(p.tile))) : Math.min(...row.map((p) => p.tile.y))
  const bars: number[] = []
  const joins: number[] = []
  grid.forEach((row, i) => {
    if (i === 0) {
      bars.push((edge + inner(row)) / 2)
      joins.push(ox)
      return
    }
    const before = [...grid[i - 1]!].sort((a, b) => a.tile.x - b.tile.x)
    let join = ox
    let best = Infinity
    for (let k = 0; k + 1 < before.length; k++) {
      const mid = (right(before[k]!.tile) + before[k + 1]!.tile.x) / 2
      if (Math.abs(mid - ox) < best) [join, best] = [mid, Math.abs(mid - ox)]
    }
    bars.push((outer(grid[i - 1]!) + inner(row)) / 2)
    joins.push(join)
  })
  grid.forEach((row, i) => {
    for (const p of row) {
      if (only && !only.has(p.id)) continue
      const pts: Point[] = [[ox, oy]]
      for (let k = 0; k <= i; k++) {
        pts.push([joins[k]!, k === 0 ? bars[0]! : bars[k - 1]!], [joins[k]!, bars[k]!])
      }
      pts.push([cx(p.face), bars[i]!], [cx(p.face), down ? p.face.y : bottom(p.tile)])
      out.push({ id: p.id, d: roundedPath(pts, radius) })
    }
  })
}
